import { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Package, FileText, Receipt, MapPin, Radar, Truck, Warehouse,
  Banknote, BarChart3, Plug, ShieldCheck, Smartphone, Leaf, Globe, LogOut, Menu, X, Users, Route, Stamp, Bell,
} from "lucide-react";
import { EbikLogo } from "@/components/EbikLogo";
import { useAuth } from "@/context/AuthContext";
import AIAssistant from "@/components/AIAssistant";
import { NotificationBell } from "@/components/portal/NotificationBell";

const CUSTOMER_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/shipments", label: "Shipments", icon: Package },
  { to: "/app/quotes", label: "Quotations", icon: FileText },
  { to: "/app/invoices", label: "Invoices", icon: Receipt },
  { to: "/app/locations", label: "Locations", icon: MapPin },
];

const STAFF_NAV = [
  { to: "/app/control-tower", label: "Control Tower", icon: Radar },
  { to: "/app/route-ai", label: "Route AI", icon: Route },
  { to: "/app/customs", label: "Customs", icon: Stamp },
  { to: "/app/alerts", label: "Alerts", icon: Bell },
  { to: "/app/executive", label: "Executive", icon: BarChart3 },
  { to: "/app/shipments", label: "Shipments", icon: Package },
  { to: "/app/fleet", label: "Fleet", icon: Truck },
  { to: "/app/warehouse", label: "Warehouse", icon: Warehouse },
  { to: "/app/crossborder", label: "Cross-Border", icon: Globe },
  { to: "/app/finance", label: "Finance", icon: Banknote, roles: ["finance_manager", "managing_director", "super_admin"] },
  { to: "/app/sustainability", label: "Sustainability", icon: Leaf },
  { to: "/app/erp", label: "ERP Console", icon: Plug, roles: ["super_admin", "managing_director", "operations_manager", "fleet_manager", "finance_manager"] },
  { to: "/app/admin", label: "Administration", icon: ShieldCheck, roles: ["super_admin", "managing_director"] },
];

export function AppLayout({ children }) {
  const { user, roleLabel, logout, isStaff } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  let items = isStaff ? STAFF_NAV : CUSTOMER_NAV;
  items = items.filter((i) => !i.roles || i.roles.includes(user.role));
  if (user.role === "driver") items = [{ to: "/app/driver", label: "Driver App", icon: Smartphone }];

  const initials = (user.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <div className="min-h-screen flex bg-muted/30">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-screen w-[260px] shrink-0 bg-[#0B0F17] text-slate-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-[68px] flex items-center px-5 border-b border-slate-800">
          <Link to="/"><EbikLogo variant="dark" size={34} /></Link>
        </div>
        <nav className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
              data-testid={`side-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}>
              <it.icon size={18} /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm">{initials}</div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user.name}</div>
              <div className="text-[11px] text-slate-500 font-mono truncate">{roleLabel}</div>
            </div>
          </div>
          <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-white">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 flex items-center justify-between px-4 bg-card border-b border-border">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="app-menu-toggle"><Menu /></button>
          <div className="lg:hidden"><EbikLogo size={30} /></div>
          <div className="hidden lg:block" />
          {isStaff ? <NotificationBell /> : <div className="w-6" />}
        </header>
        <div className="flex-1 p-4 lg:p-8 max-w-[1500px] w-full mx-auto">{children}</div>
      </div>

      {user.role !== "driver" && <AIAssistant />}
    </div>
  );
}
